import { Link } from "react-router-dom";
import { useData } from "data/data-provider";
import moment from "moment";

/**
 * Design B brand mark.
 *
 * Renders the FMS-uploaded company logo when available; otherwise falls back
 * to a clean wordmark + car icon so the header never looks naked when a
 * company hasn't uploaded a logo yet. Mirrors `shared/Logo/Logo` API
 * (uses `companyDetails.company_logo` and `setSelectedWeeks` reset on click).
 */
const Logo = () => {
  const { companyDetails, setSelectedWeeks } = useData();
  const time = moment();
  const timeInMilliseconds = time.valueOf();

  const companyLogoUrl = companyDetails?.company_logo
    ? `${companyDetails.company_logo}?date=${timeInMilliseconds}`
    : "";

  const companyName: string =
    companyDetails?.company_name?.trim() ||
    companyDetails?.company?.name?.trim() ||
    "";

  // First letter or two for the badge fallback
  const initials = companyName
    ? companyName
        .split(/\s+/)
        .map((w) => w[0])
        .filter(Boolean)
        .join("")
        .slice(0, 2)
        .toUpperCase()
    : "";

  const handleClick = () => {
    setSelectedWeeks([]);
  };

  return (
    // No explicit text color on the wrapper — the Logo INHERITS from its
    // parent's text color. That way:
    //   - In the Header (sits inside `text-text-color` page wrapper) the
    //     wordmark renders in navy on the cream banner.
    //   - In the Footer (`text-footer-text-color`) the wordmark renders in
    //     cream on the dark navy footer, instead of disappearing into the
    //     bg as it did when this was hardcoded to `text-text-color`.
    <Link
      to="/"
      onClick={handleClick}
      className="ttnc-logo group inline-flex items-center gap-3 focus:outline-none focus:ring-0"
      aria-label={companyName || "Home"}
    >
      {companyLogoUrl ? (
        <img
          src={companyLogoUrl}
          alt={companyName ? `${companyName} logo` : "Logo"}
          className="h-12 w-auto max-h-12 object-contain transition-transform duration-300 group-hover:scale-[1.03]"
        />
      ) : (
        <>
          {/* Car mark in primary gold */}
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-primary-200 to-primary-300 shadow-md shadow-primary-200/30 transition-transform duration-300 group-hover:scale-105">
            {initials ? (
              <span className="text-base font-extrabold tracking-tight text-button-color">
                {initials}
              </span>
            ) : (
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-button-color"
                aria-hidden="true"
              >
                {/* Car silhouette: body + cabin, with two wheels */}
                <path d="M14 16H9m10 0h3v-3.15a1 1 0 0 0-.84-.99L16 11l-2.7-3.6a1 1 0 0 0-.8-.4H5.24a2 2 0 0 0-1.8 1.1l-.8 1.63A6 6 0 0 0 2 12.42V16h2" />
                <circle cx="6.5" cy="16.5" r="2.5" />
                <circle cx="16.5" cy="16.5" r="2.5" />
              </svg>
            )}
          </span>
          {companyName && (
            <span className="hidden sm:flex flex-col leading-tight">
              {/* No explicit color — inherits from the wrapper Link, which in
                  turn inherits from the surrounding context (page text-color
                  in the header, footer-text-color in the footer). */}
              <span className="text-base font-extrabold tracking-tight">
                {companyName}
              </span>
              <span className="text-[10px] font-medium uppercase tracking-[0.18em] opacity-70">
                Premium Rentals
              </span>
            </span>
          )}
        </>
      )}
    </Link>
  );
};

export default Logo;
